import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, AfterViewInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BarLineComponent } from '../../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import { DropdownComponent, DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { SelectionComponent } from '../../../../../shared/components/ui/selection/selection.component';
import { ApiService } from '../../../../../core/services/api.service';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

interface CohortData {
  start: string;
  end: string;
  unit_date: string;
  cohort_size: number;
  active_users: number;
  retention_rate: number;
}

interface ChartData {
  start: string;
  end: string;
  unit_date: string;
  active_users: number;
  retention_rate: number;
  month_short?: string;
  cohort_size?: number;
  week?: string;
}

@Component({
  standalone: true,
  selector: 'app-business-cohort-group',
  imports: [
    CommonModule,
    FormsModule,
    BarLineComponent,
    DropdownComponent,
    SelectionComponent,
    IndividualToasterComponent
],
  templateUrl: './business-cohort-group.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-cohort-group.component.scss'
})
export class BusinessCohortGroupComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits'; // Dynamic product server name
  @Input() requiresAccessToken: boolean = false; // Whether the product requires an access token
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  @ViewChild('yearDropdown') yearDropdown?: DropdownComponent;
  @ViewChild('monthDropdown') monthDropdown?: DropdownComponent;
  @ViewChild('weekDropdown') weekDropdown?: DropdownComponent;
  isLoading = signal(false)
  // Dropdown options
  yearOptions: DropdownOption[] = [];
  monthOptions: DropdownOption[] = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  weekOptions: DropdownOption[] = [];

  // Timeline selection options (for radio buttons)
  timelineOptions = [
    { value: 'year', label: 'Year' },
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' }
  ];

  // Selected values
  selectedYear: string = '2025';
  selectedMonth: string = '1'; // January
  selectedWeek: string = '1'; // Week 1
  selectedTimeline: string = 'month';

  // Initial values for dropdowns
  initialYear: string = '';
  initialMonth: string = '';
  initialWeek: string = '';

  // Chart data
  chartData: ChartData[] = [];
  
  // Store API cohort data
  cohortData: CohortData[] = [];
  
  // Cohort stats
  cohortStats = {
    period: '',
    registration: 0
  };

  // Chart colors
  colors = {
    data1: '#8b5cf6' as `#${string}` // blue-500 for bars
  };

  lineColor = '#fbbf24' as `#${string}`; // amber-400 for retention rate line
  toolTipData: ({ label: string; key: string; type?: string; prefix?: string; })[] = [];

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.generateYearOptions();
    this.initializeDefaultSelection();
    this.fetchCohortData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && !changes['dateRange'].firstChange) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      this.fetchCohortData();
    }
  }

  ngAfterViewInit(): void {
    // Set initial values in the dropdowns after view is initialized
    setTimeout(() => {
      if (this.yearDropdown) {
        this.yearDropdown.writeValue(this.initialYear);
      }
      if (this.monthDropdown) {
        this.monthDropdown.writeValue(this.initialMonth);
      }
      if (this.weekDropdown) {
        this.weekDropdown.writeValue(this.initialWeek);
      }
    }, 0);
  }

  generateYearOptions(): void {
    const currentYear = new Date().getFullYear();
    for (let year = 2017; year <= currentYear; year++) {
      this.yearOptions.push({
        value: year.toString(),
        label: year.toString()
      });
    }
  }

  generateWeekOptions(year: number): void {
    this.weekOptions = [];
    
    // Get current date for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Week 1 starts on January 1st
    let currentWeekStart = new Date(year, 0, 1);
    let weekNumber = 1;
    
    while (currentWeekStart.getFullYear() === year) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6); // Add 6 days for a 7-day week
      
      // Adjust week end if it goes into next year
      const actualWeekEnd = weekEnd.getFullYear() > year 
        ? new Date(year, 11, 31) // Last day of current year
        : weekEnd;
      
      // Stop if the week start is in the future (for current year)
      if (year === today.getFullYear() && currentWeekStart > today) {
        break;
      }
      
      // Format dates
      const formatDate = (date: Date) => {
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month}/${day}`;
      };
      
      const startStr = formatDate(currentWeekStart);
      const endStr = formatDate(actualWeekEnd);
      
      this.weekOptions.push({
        value: weekNumber.toString(),
        label: `Week ${weekNumber} (${startStr} - ${endStr})`
      });
      
      // Move to next week
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      weekNumber++;
      
      // Safety check to prevent infinite loop (max 53 weeks in a year)
      if (weekNumber > 53) break;
    }
  }

  initializeDefaultSelection(): void {
    const currentDate = new Date();
    this.selectedYear = currentDate.getFullYear().toString();
    
    // Generate week options for the selected year
    this.generateWeekOptions(parseInt(this.selectedYear));
    
    // Set initial values for dropdowns
    this.initialYear = this.selectedYear;
    this.initialMonth = this.selectedMonth;
    this.initialWeek = this.selectedWeek;
  }

  getCurrentWeekNumber(): number {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const firstDayOfYear = new Date(year, 0, 1); // January 1st
    
    // Calculate days between January 1st and current date
    const daysDiff = Math.floor((currentDate.getTime() - firstDayOfYear.getTime()) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysDiff / 7) + 1;
    
    return Math.max(1, Math.min(weekNumber, 53));
  }

  onYearChange(selectedValue: string | string[]): void {
    this.selectedYear = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    
    // Regenerate week options for the new year
    this.generateWeekOptions(parseInt(this.selectedYear));
    
    // Reset week selection to week 1 when year changes
    this.selectedWeek = '1';
    if (this.weekDropdown) {
      this.weekDropdown.writeValue('1');
    }
    
    this.fetchCohortData();
  }

  onMonthChange(selectedValue: string | string[]): void {
    this.selectedMonth = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    this.fetchCohortData();
  }

  onWeekChange(selectedValue: string | string[]): void {
    this.selectedWeek = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
    this.fetchCohortData();
  }

  onTimelineChange(value: string | string[]): void {
    this.selectedTimeline = Array.isArray(value) ? value[0] : value;
    
    // Initialize the appropriate dropdown when timeline changes
    setTimeout(() => {
      if (this.selectedTimeline === 'month' && this.monthDropdown) {
        this.monthDropdown.writeValue(this.selectedMonth);
      } else if (this.selectedTimeline === 'week' && this.weekDropdown) {
        this.weekDropdown.writeValue(this.selectedWeek);
      }
    }, 0);
    
    this.fetchCohortData();
  }

  fetchCohortData(): void {
    // Construct the cohort start date based on selected timeline
    const year = parseInt(this.selectedYear);
    const month = parseInt(this.selectedMonth);
    const week = parseInt(this.selectedWeek);

    const payload: any = {
      // timezone: 'PDT',
      outputFormat: 'db',
      year: year,
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    // Add month or week based on timeline selection
    if (this.selectedTimeline === 'month') {
      payload.month = month;
    } else if (this.selectedTimeline === 'week') {
      payload.week = week;
    }
    // For 'year' timeline, only year is needed
this.isLoading.set(true)
    this.apiService
      .post('/visionboard/stats/business_cohort_analysis', payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0) {
            this.isLoading.set(false)
           
            
            // Extract cohort info for the stats card
            if (response.cohort_group) {
              const cohortInfo = response.cohort_group;
              const startDate = new Date(cohortInfo.startTime);
              const endDate = new Date(cohortInfo.endTime);
              
              // Format dates
              const formatDate = (date: Date) => {
                const day = date.getDate().toString().padStart(2, '0');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${day} ${monthNames[date.getMonth()]}`;
              };

              this.cohortStats = {
                period: `${formatDate(startDate)} - ${formatDate(endDate)}`,
                registration: cohortInfo.totalSignups || 0
              };

              this.toolTipData = [
                { label: 'Cohort size:', key: 'cohort_size' },
                { label: 'Active users from cohort:', key: 'active_users' },
                { label: 'Retention Rate:', key: 'retention_rate', type: 'percent', prefix: '%' }
              ];
            }
            // Transform retention data for chart
            if (response.retentionData && Array.isArray(response.retentionData)) {
              this.chartData = response.retentionData.map((item: any, index: number) => {
                // For month/year timeline, use month_short from API or generate it
                // For week timeline, generate a week label
                let monthShort = '';
                let weekLabel = '';
                if (this.selectedTimeline !== 'week') {
                  if (item.month_short) {
                    monthShort = item.month_short;
                  } else if (item.start) {
                    const date = new Date(item.start);
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    monthShort = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                  }
                } else {
                  if (item.week !== undefined && item.week !== null && item.week !== '') {
                    weekLabel = typeof item.week === 'number' || !isNaN(Number(item.week)) 
                      ? `Week ${item.week}` 
                      : item.week.toString();
                  } else if (item.start) {
                    const date = new Date(item.start);
                    if (!isNaN(date.getTime())) {
                      const dCopy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                      dCopy.setUTCDate(dCopy.getUTCDate() + 4 - (dCopy.getUTCDay() || 7));
                      const yearStart = new Date(Date.UTC(dCopy.getUTCFullYear(), 0, 1));
                      const weekNum = Math.ceil((((dCopy.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
                      weekLabel = `Week ${weekNum}`;
                    } else {
                      weekLabel = `Week ${index + 1}`;
                    }
                  } else {
                    weekLabel = `Week ${index + 1}`;
                  }
                }

                return {
                  start: item.start || '',
                  end: item.end || '',
                  unit_date: item.unit_date || item.start || '',
                  active_users: item.active_users || 0,
                  retention_rate: item.retention_rate || 0,
                  month_short: monthShort,
                  cohort_size: item.cohort_size || 0,
                  week: weekLabel
                };
              });
            } else {
              this.chartData = [];
            }
          } else {
            this.isLoading.set(false)
            // No data or error in response
            this.cohortStats = {
              period: 'No data',
              registration: 0
            };
            this.chartData = [];
          }
        },
        error: (error: any) => {
          this.isLoading.set(false)
          console.error('Error fetching cohort group data:', error);
          this.cohortData = [];
          this.chartData = [];
          this.cohortStats = {
            period: 'No data',
            registration: 0
          };
        }
      });
  }



}
